import { BeamButtonProps, Button, Css, useTestIds } from "@homebound/beam";
import { StepActions } from "src/components/stepper/StepActions";
import { useStepperContext } from "src/components/stepper/StepperContext";

type StepNavProps = {
  /** URL to navigate to when the user cancels out of the flow. */
  cancelUrl: string;
  /** Disables save actions when the form is invalid or read-only. Navigation is always enabled. */
  saveDisabled?: BeamButtonProps["disabled"];
  /**
   * Runs the shared `save()` and owns post-save navigation. Used by both "Save & exit" (first step)
   * and the final "Save" button.
   */
  onSave: () => Promise<void>;
  /** Label for the final step's primary save button (e.g. "Create" vs "Save Options"). */
  saveLabel?: string;
};

/**
 * Renders the stepper's footer actions into the stepper bar via `StepActions`.
 *
 * - First step (Details): Cancel · Save & exit · Next
 * - Last step (Variants): Cancel · Back · Save
 *
 * The stepper uses a single shared formState, so both "Save & exit" and "Save" call the same
 * `onSave`, which owns navigation to the new rpog/draft URL.
 */
export function StepNav(props: StepNavProps) {
  const { cancelUrl, saveDisabled, onSave, saveLabel = "Save Options" } = props;
  const { nextStep, prevStep, isLastStep } = useStepperContext();
  const tid = useTestIds({}, "stepNav");

  return (
    <StepActions>
      <div css={Css.df.aic.gap1.$}>
        <Button label="Cancel" variant="quaternary" onClick={cancelUrl} {...tid.cancel} />
        {isLastStep ? (
          <>
            <Button label="Back" variant="secondary" onClick={prevStep} {...tid.back} />
            <Button label={saveLabel} disabled={saveDisabled} onClick={onSave} {...tid.save} />
          </>
        ) : (
          <>
            <Button
              label="Save & exit"
              variant="secondary"
              disabled={saveDisabled}
              onClick={onSave}
              {...tid.saveAndExit}
            />
            <Button label="Next" onClick={nextStep} {...tid.next} />
          </>
        )}
      </div>
    </StepActions>
  );
}
